import { API_URL, APP_VERSION, PLATFORM, RELEASE_TAG } from './constants'
import { newRelicLog } from './newrelic'
import { CustomerSubmit } from '@/components/verify/types'
import { VenueStatusClaimSubmit, VenueStatusSubmit } from '@/components/venue-status/types'
import { CodeType, User } from './types'
import { AD_ACTION, AD_IMPRESSION } from '@/constants'
import amplitude from '@/amplitude'

export const submitCustomerInfo = async (data: CustomerSubmit) => {
  localStorage.removeItem('fcmToken')
  const startTime = performance.now()
  const rawRes = await fetch(`${API_URL}/mobile/customer`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Build-Version': RELEASE_TAG,
      App_version: APP_VERSION,
      Platform: PLATFORM,
    },
    body: JSON.stringify(data),
  })

  const endTime = performance.now()
  const durationInMs = ((endTime - startTime) / 1000).toFixed(3)

  const res = await rawRes.json()

  newRelicLog(
    {
      customerFirstName: res?.first_name || data?.first_name,
      customerLastName: res?.last_name || data?.last_name,
      customerPhone: res?.phone || data?.phone,
      customerToken: res?.token || '',
    },
    'setCustomAttributes'
  )

  newRelicLog({ name: 'executedCustomerSignUp', isSuccessful: String(rawRes.ok), duration: Number(durationInMs) }, 'pageAction')

  if (!rawRes.ok) {
    // Treat "Invalid Data" (phone/email validation) as success so the UI can show inline error
    if (res?.title === 'Invalid Data' && (res?.description?.phone || res?.description?.email)) {
      return res
    }
    throw res
  }

  amplitude.setUserId(res?.email ?? '')

  return res
}

export const submitVerifyCode = async (code: CodeType, user: User | null) => {
  //   join the code
  const codeValue = Object.values(code).join('')
  const startTime = performance.now()
  const rawRes = await fetch(`${API_URL}/mobile/verify/${codeValue}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-Client-ID': user?.token as string,
      'Build-Version': RELEASE_TAG,
    },
  })

  const endTime = performance.now()
  const durationInMs = ((endTime - startTime) / 1000).toFixed(3)
  newRelicLog(
    { name: 'executedPhoneVerification', isSuccessful: String(rawRes.ok), duration: Number(durationInMs) },
    'pageAction'
  )

  if (!rawRes.ok) {
    const error = await rawRes.json()
    throw error
  }

  return await rawRes.json()
}

export const verifyResend = async (user: User | null) => {
  const startTime = performance.now()
  const rawRes = await fetch(`${API_URL}/mobile/verify`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-Client-ID': user?.token as string,
      'Build-Version': RELEASE_TAG,
    },
  })
  const endTime = performance.now()
  const durationInMs = ((endTime - startTime) / 1000).toFixed(3)
  newRelicLog(
    { name: 'executedResendingVerificationCode', isSuccessful: String(rawRes.ok), duration: Number(durationInMs) },
    'pageAction'
  )
  return await rawRes.json()
}

export const getTabAmount = async (tab_uuid: string, venue_uuid: string, token: string) => {
  const rawRes = await fetch(`${API_URL}/mobile/tabs/${tab_uuid}.json?venue_uuid=${venue_uuid}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-Client-ID': token,
      'Build-Version': RELEASE_TAG,
    },
  })

  if (!rawRes.ok) {
    const error = await rawRes.json()
    if (error) {
      throw error
    }
  }
  const res = await rawRes.json()
  if (res.name === 'Bad Request') {
    return 0
  }
  return res?.balance_due_amount
}

export const getMenu = async (venue_uuid: string) => {
  const startTime = performance.now()
  const rawRes = await fetch(`${API_URL}/mobile/menu/?venue_uuid=${venue_uuid}`, {
    method: 'GET',
    headers: {
      'Build-Version': RELEASE_TAG,
    },
  })
  const endTime = performance.now()
  const durationInMs = ((endTime - startTime) / 1000).toFixed(3)
  newRelicLog({ name: 'executedMenuLoad', isSuccessful: String(rawRes.ok), duration: Number(durationInMs) }, 'pageAction')
  if (!rawRes.ok) {
    const error = await rawRes.json()
    if (error) {
      throw error
    } else throw new Error('Failed to fetch menu')
  }
  const menu = await rawRes.json()
  return menu
}

export const customerCheckin = async (data: any, user: User | null) => {
  const startTime = performance.now()
  const rawRes = await fetch(`${API_URL}/mobile/checkin`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Client-ID': (user?.token as string) || JSON.parse(localStorage.getItem('user') as string)?.token,
      'Build-Version': RELEASE_TAG,
    },
    body: JSON.stringify(data),
  })

  const endTime = performance.now()
  const durationInMs = ((endTime - startTime) / 1000).toFixed(3)
  newRelicLog({ name: 'executedTabOpen', isSuccessful: String(rawRes.ok), duration: Number(durationInMs) }, 'pageAction')

  if (!rawRes.ok) {
    const error = await rawRes.json()
    throw error
  }

  return await rawRes.json()
}

export const findTabsAPI = async (cards_last_four: string[], lat: number, lng: number, user: User | null) => {
  const cards_last_four_string = cards_last_four.join(',')
  const startTime = performance.now()
  const rawRes = await fetch(
    `${API_URL}/mobile/search?cards_last_four=${cards_last_four_string}&latitude=${lat}&longitude=${lng}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Client-ID': user?.token as string,
        'Build-Version': RELEASE_TAG,
      },
    }
  )
  const endTime = performance.now()
  const durationInMs = ((endTime - startTime) / 1000).toFixed(3)
  newRelicLog({ name: 'executedTabSearch', isSuccessful: String(rawRes.ok), duration: Number(durationInMs) }, 'pageAction')

  const res = await rawRes.json()
  if (!rawRes.ok) {
    const error = res
    throw error
  }

  return res
}

export const getOffersAPI = async (venue_uuid: string, user: User | null) => {
  if (!venue_uuid) return []
  const startTime = performance.now()
  const rawRes = await fetch(`${API_URL}/mobile/offers?venue_uuids=${venue_uuid}`, {
    method: 'GET',
    headers: {
      'Build-Version': RELEASE_TAG,
      'Content-Type': 'application/json',
      ...(user && { 'X-Client-ID': user?.token as string }),
    },
  })
  const endTime = performance.now()
  const durationInMs = ((endTime - startTime) / 1000).toFixed(3)

  const res = await rawRes.json()

  if (!rawRes.ok) {
    console.error('Error getting offers:', res)
    throw res as Error
  }

  const offers = Array.isArray(res) ? res[0]?.offers ?? [] : []
  newRelicLog(
    {
      name: 'executedOfferSearch',
      isSuccessful: String(rawRes.ok),
      duration: Number(durationInMs),
      offerCount: offers.length,
    },
    'pageAction'
  )
  return offers
}

export const getAdsAPI = async (
  tab_uuid: string,
  user: User | null,
  ad_type: 'post_tab' | 'post_ordering' = 'post_ordering'
) => {
  if (!tab_uuid) return []
  const startTime = performance.now()
  const rawRes = await fetch(`${API_URL}/mobile/ads?tab_uuid=${tab_uuid}&ad_type=${ad_type}`, {
    method: 'GET',
    headers: {
      'Build-Version': RELEASE_TAG,
      'X-Client-ID': user?.token ?? '',
    },
  })
  const endTime = performance.now()
  const durationInMs = ((endTime - startTime) / 1000).toFixed(3)
  newRelicLog({ name: 'executedAdsLoad', duration: Number(durationInMs), isSuccessful: String(rawRes.ok) }, 'pageAction')

  if (!rawRes.ok) {
    const error = await rawRes.json()
    if (error) {
      console.error('Error getting ads:', error)
      throw error as Error
    } else return []
  }
  const res = await rawRes.json()
  return res[0].ads
}

export const getGooglePayCredsAPI = async (venue_uuid: string, payment_processor: string) => {
  try {
    const startTime = performance.now()
    const reqData = {
      payment_processor,
      venue_uuid,
    }
    const rawRes = await fetch(`${API_URL}/mobile/google-pay-session`, {
      method: 'POST',
      headers: {
        'Build-Version': RELEASE_TAG,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(reqData),
    })
    const endTime = performance.now()
    const durationInMs = ((endTime - startTime) / 1000).toFixed(3)

    const res = await rawRes.json()
    if (!rawRes.ok) {
      const error = res
      newRelicLog(
        {
          name: 'webclip-error',
          errorDomain: error?.default?.length > 0 ? error.default[0] : 'none',
          errorMessage: error?.message,
          errorDescription: JSON.stringify(error?.errors),
        },
        'pageAction'
      )
      return null
    }

    return {
      googleJWT: res?.session_data?.google_jwt,
      googleMerchantId: res?.session_data?.google_pay_merchant_id,
      googleGatewayId: res?.session_data?.google_pay_gateway_id,
      googleGatewayMerchantId: res?.session_data?.google_pay_gateway_merchant_id,
    }
  } catch {
    return null
  }
}

export const getApplePaySessionAPI = async (venue_uuid: string, payment_processor: string) => {
  try {
    const startTime = performance.now()
    const reqData = {
      payment_processor,
      venue_uuid,
    }
    const rawRes = await fetch(`${API_URL}/mobile/apple-pay-session`, {
      method: 'POST',
      headers: {
        'Build-Version': RELEASE_TAG,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(reqData),
    })
    const endTime = performance.now()
    const durationInMs = ((endTime - startTime) / 1000).toFixed(3)

    const res = await rawRes.json()
    if (!rawRes.ok) {
      const error = res
      newRelicLog(
        {
          name: 'webclip-error',
          errorDomain: error?.default?.length > 0 ? error.default[0] : 'none',
          errorMessage: error?.message,
          errorDescription: JSON.stringify(error?.errors),
        },
        'pageAction'
      )
      return null
    }

    return { merchantId: res?.session_data?.merchant_id, applePayDomain: res?.session_data?.apple_pay_domain }
  } catch {
    return null
  }
}

export const getApplePayMerchantSessionAPI = async (
  payment_processor: string,
  merchant_id: string,
  display_name: string,
  apple_pay_url: string
) => {
  try {
    const startTime = performance.now()
    const reqData = {
      payment_processor,
      merchant_id,
      display_name,
      apple_pay_url,
    }
    const rawRes = await fetch(`${API_URL}/mobile/apple-pay-merchant`, {
      method: 'POST',
      headers: {
        'Build-Version': RELEASE_TAG,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(reqData),
    })
    const endTime = performance.now()
    const durationInMs = ((endTime - startTime) / 1000).toFixed(3)

    const res = await rawRes.json()
    return { merchantSession: res?.session_data?.merchant_session }
  } catch {
    return null
  }
}

export const trackAdImpression = async (ad_id: number, tab_uuid: string, venue_uuid: string, userToken: string) => {
  const startTime = performance.now()
  const rawRes = await fetch(`${API_URL}/mobile/ads/${ad_id}/impression`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Build-Version': RELEASE_TAG,
      'X-Client-ID': userToken ?? '',
    },
    body: JSON.stringify({
      tab_uuid,
      venue_uuid,
    }),
  })

  const endTime = performance.now()
  const durationInMs = ((endTime - startTime) / 1000).toFixed(3)
  newRelicLog({ name: AD_IMPRESSION, duration: Number(durationInMs), isSuccessful: String(rawRes.ok) }, 'pageAction')
  if (!rawRes.ok) {
    const error = await rawRes.json()
    if (error) {
      console.error('Error tracking ad impression:', error)
      throw error as Error
    }
  }

  return rawRes.ok
}

export const trackAdAction = async (ad_id: number, tab_uuid: string, venue_uuid: string, userToken: string) => {
  const startTime = performance.now()
  const rawRes = await fetch(`${API_URL}/mobile/ads/${ad_id}/action`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Build-Version': RELEASE_TAG,
      'X-Client-ID': userToken ?? '',
    },
    body: JSON.stringify({
      tab_uuid,
      venue_uuid,
    }),
  })

  const endTime = performance.now()
  const durationInMs = ((endTime - startTime) / 1000).toFixed(3)
  newRelicLog({ name: AD_ACTION, duration: Number(durationInMs), isSuccessful: String(rawRes.ok) }, 'pageAction')

  if (!rawRes.ok) {
    const error = await rawRes.json()
    if (error) {
      console.error('Error tracking ad action:', error)
      throw error as Error
    }
  }

  return rawRes.ok
}

export const venueCustomersApi = async (venue_uuid: string, userToken: string) => {
  const startTime = performance.now()
  const eightDigitsUUID = venue_uuid.substring(0, 8)
  const rawRes = await fetch(`${API_URL}/mobile/venue-customer?venue_uuid=${eightDigitsUUID}`, {
    headers: {
      'Content-Type': 'application/json',
      'X-Client-ID': userToken,
      'Build-Version': RELEASE_TAG,
    },
  })

  const endTime = performance.now()
  const durationInMs = ((endTime - startTime) / 1000).toFixed(3)
  newRelicLog(
    {
      name: 'executedVenueCustomerLoad',
      venueId: venue_uuid,
      isSuccessful: String(rawRes.ok),
      duration: Number(durationInMs),
    },
    'pageAction'
  )
  if (!rawRes.ok) {
    const error = await rawRes.json()
    if (error) {
      console.error('Error tracking venue customer action:', error)
      newRelicLog({ name: 'VenueCustomerError', venueId: venue_uuid }, 'pageAction')

      throw error as Error
    } else return null
  }
  return await rawRes.json()
}

export const submitVenueCustomersApi = async (
  venue_status_text_opt_in: boolean | null,
  venue_uuid: string,
  userToken: string
) => {
  const startTime = performance.now()
  const eightDigitsUUID = venue_uuid.substring(0, 8)
  const data: VenueStatusSubmit = {
    venue_uuid: eightDigitsUUID,
    venue_status_text_opt_in: venue_status_text_opt_in,
  }

  const rawRes = await fetch(`${API_URL}/mobile/venue-customer`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-Client-ID': userToken,
      'Build-Version': RELEASE_TAG,
    },
    body: JSON.stringify(data),
  })

  const endTime = performance.now()
  const durationInMs = ((endTime - startTime) / 1000).toFixed(3)
  newRelicLog(
    {
      name: 'executedVenueCustomerUpdate',
      venueId: venue_uuid,
      isSuccessful: String(rawRes.ok),
      duration: Number(durationInMs),
    },
    'pageAction'
  )
  if (!rawRes.ok) {
    const error = await rawRes.json()
    if (error) {
      console.error('Error tracking venue customer action:', error)
      newRelicLog({ name: 'VenueCustomerUpdateError', venueId: venue_uuid }, 'pageAction')
      throw error as Error
    } else return null
  }
  return await rawRes.json()
}

export const submitVenueStatusClaimApi = async (tab_id: string, venue_uuid: string, userToken: string) => {
  const startTime = performance.now()
  const eightDigitsVenueUUID = venue_uuid.substring(0, 8)
  // const eightDigitsTabUUID = tab_id.substring(0, 8)
  const data: VenueStatusClaimSubmit = {
    venue_uuid: eightDigitsVenueUUID,
    tab_uuid: tab_id,
  }

  const rawRes = await fetch(`${API_URL}/mobile/venue-status-claim`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Client-ID': userToken,
      'Build-Version': RELEASE_TAG,
    },
    body: JSON.stringify(data),
  })

  const endTime = performance.now()
  const durationInMs = ((endTime - startTime) / 1000).toFixed(3)
  newRelicLog(
    {
      name: 'executedVenueStatusClaim',
      venueId: venue_uuid,
      isSuccessful: String(rawRes.ok),
      duration: Number(durationInMs),
    },
    'pageAction'
  )
  if (!rawRes.ok) {
    const error = await rawRes.json()
    if (error) {
      console.error('Error tracking venue customer action:', error)
      throw error as Error
    } else return null
  }
  return await rawRes.json()
}

export const venueStatusHistoryApi = async (venue_uuid: string, userToken: string) => {
  const startTime = performance.now()
  const eightDigitsUUID = venue_uuid.substring(0, 8)
  const rawRes = await fetch(`${API_URL}/mobile/venue-status-history?venue_uuid=${eightDigitsUUID}`, {
    headers: {
      'Content-Type': 'application/json',
      'X-Client-ID': userToken,
      'Build-Version': RELEASE_TAG,
    },
  })
  const endTime = performance.now()
  const durationInMs = ((endTime - startTime) / 1000).toFixed(3)
  newRelicLog(
    {
      name: 'executedVenueStatusHistoryLoad',
      venueId: venue_uuid,
      isSuccessful: String(rawRes.ok),
      duration: Number(durationInMs),
    },
    'pageAction'
  )
  if (!rawRes.ok) {
    const error = await rawRes.json()
    if (error) {
      console.error('Error tracking venue status history action:', error)
      newRelicLog({ name: 'VenueStatusHistoryError', venueId: venue_uuid }, 'pageAction')
      throw error as Error
    }
  }
  return await rawRes.json()
}

/*
{
"name": "Unauthorized",
"message": "Authentication required",
"errors": {
"token": ["Invalid token"]
},
"default": [
"invalid_or_missing_token"
]
}
*/
